/**
 * `useResourceCopyMoveFromAgent` — CCM "smoothest UX" §1.7 (consistency with
 * the chat/NL path).
 *
 * The concierge will gain `lx_copy_resource` / `lx_move_resource` tools (a
 * SEPARATE runtime build — NOT built here) so the lawyer can also say "move
 * the draft to Trend Casual" in chat. When such a tool runs it rides the
 * gateway's automatic `session.tool` broadcast (same channel as
 * `useSidecarUpdatedStream` / `useFileOpenFromAgent`). This hook listens for
 * those tools' completion and invalidates the workspace-tree cache so the
 * file tree stays consistent whether the lawyer DRAGGED, used the picker, or
 * ASKED — closing the loop the SPA-side mutation (`useResourceCopyMove`)
 * already closes for the direct paths.
 *
 * Because the future tool's `toolInput`/`toolResult` scope shape is NOT yet
 * specified (it's a parallel runtime build), this invalidates the WHOLE
 * `["case-workspace-tree"]` query prefix — every cached scope (the org tree +
 * any case tree). A copy/move touches at most two scopes; a prefix-wide
 * invalidation is correct (refetches only what's currently mounted) and
 * robust to the unspecified payload — exactly the "invalidate on the same
 * query keys" the handoff asks for, without guessing the envelope.
 *
 * Mount once per workspace-tree-bearing layout (CaseWorkspaceLayout +
 * GlobalConversationLayout), beside `useFileOpenFromAgent`.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useEventStream } from "@/hooks/useEventStream";

interface AgentToolEventRuntime {
  data?: {
    phase?: string;
    toolName?: string;
    toolResult?: { details?: { status?: string } };
  };
}

/** The concierge tool names that perform a cross-scope copy/move (§1.7). */
const COPY_MOVE_TOOLS = new Set<string>([
  "lx_copy_resource",
  "lx_move_resource",
]);

/**
 * Pure predicate (exported for unit test): does this `session.tool` payload
 * signal a COMPLETED, non-failed concierge copy/move? Only the `"end"` phase
 * qualifies (the resource has landed on disk, so a refetch sees it).
 */
export function isCopyMoveToolEnd(payload: unknown): boolean {
  const typed = payload as AgentToolEventRuntime;
  if (typed?.data?.phase !== "end") return false;
  const toolName = typed.data?.toolName;
  if (!toolName || !COPY_MOVE_TOOLS.has(toolName)) return false;
  if (typed.data?.toolResult?.details?.status === "failed") return false;
  return true;
}

export function useResourceCopyMoveFromAgent(): void {
  const queryClient = useQueryClient();
  useEventStream("session.tool", (payload) => {
    if (!isCopyMoveToolEnd(payload)) return;
    // Prefix-wide invalidation — refreshes whichever scope's tree is mounted
    // (org and/or case), keyed identically to `useCaseWorkspaceTree`.
    void queryClient.invalidateQueries({ queryKey: ["case-workspace-tree"] });
  });
}
